<template>
  <e-sidebar
    id="sidebar-form-tier-promotion"
    :title="isTierUpdate ? $t('Editar faixa da Promoção') : $t('Nova faixa da Promoção')"
    :show="showSidebar"
    :fetching="fetching"
    :saving="saving"
    width="500px"
    @save="onSaveTier"
    @hidden="onHideSidebar"
  >
    <template #content>
      <FormulateForm
        ref="tierSidebarForm"
        name="tierSidebarForm"
      >
        <b-row>
          <b-col md="6">
            <FormulateInput
              id="tier-minimum-amount"
              v-model="tierForm.minimumAmount"
              type="text-number"
              currency="R$"
              :precision="2"
              :label="$t('Valor mínimo')"
              class="required"
              validation="required"
            />
          </b-col>
        </b-row>

        <b-row class="mt-1">
          <b-col>
            <h6>{{ $t('Brindes') }}</h6>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <e-search-sku
              id="tier-reward-sku"
              v-model="rewardForm.sku"
              :required="false"
              value-is-object
              only-active
            />
          </b-col>
        </b-row>

        <b-row class="align-items-end">
          <b-col md="6">
            <FormulateInput
              id="tier-reward-quantity"
              v-model="rewardForm.quantity"
              type="text-number"
              :precision="4"
              :label="$t('Quantidade')"
            />
          </b-col>
          <b-col
            md="6"
            class="d-flex justify-content-end mb-1"
          >
            <e-button
              variant="primary"
              :text="$t('Adicionar brinde')"
              @click="addReward"
            />
          </b-col>
        </b-row>

        <b-row class="mt-2">
          <b-col>
            <b-table
              ref="rewardsTable"
              show-empty
              responsive
              class="bordered"
              :fields="rewardFields"
              :items="activeRewards"
              :empty-text="$t('Nenhum brinde adicionado')"
            >
              <template #cell(sku)="row">
                {{ row.item.sku && row.item.sku.name }}
              </template>

              <template #cell(action)="row">
                <e-grid-actions
                  :show-update="false"
                  :show-delete="true"
                  @delete="removeReward(row)"
                />
              </template>
            </b-table>
          </b-col>
        </b-row>
      </FormulateForm>
    </template>
  </e-sidebar>
</template>

<script>
import { BRow, BCol, BTable } from 'bootstrap-vue'
import { mapActions } from 'vuex'
import ESidebar from '@/views/components/ESidebar.vue'
import ESearchSku from '@/views/components/inputs/ESearchSku.vue'
import { EButton, EGridActions } from '@/views/components'
import { localListAddItem, localListDeleteItem } from '@/store/utils'
import {
  getInitialSpendAndGetTierForm,
  getInitialSpendAndGetRewardForm,
} from '@/store/pages/sale/promotion/promotion-maintain'

export default {
  components: { BRow, BCol, BTable, ESidebar, ESearchSku, EButton, EGridActions },

  props: {
    tiers: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isTierUpdate: false,
      showSidebar: false,
      fetching: false,
      saving: false,
      tierForm: getInitialSpendAndGetTierForm(),
      rewardForm: getInitialSpendAndGetRewardForm(),
    }
  },

  computed: {
    activeRewards() {
      return this.tierForm.rewards.filter(reward => !reward.isDeleted)
    },

    rewardFields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '80px' },
        },
        {
          label: this.$t('Produto'),
          key: 'sku',
          thClass: 'text-center',
          tdClass: 'text-left',
        },
        {
          label: this.$t('Quantidade'),
          key: 'quantity',
          thClass: 'text-center',
          tdClass: 'text-right',
          thStyle: { width: '120px' },
        },
      ]
    },
  },

  methods: {
    ...mapActions('pages/sale/promotion/promotionMaintain', {
      stAddSpendAndGetTier: 'addSpendAndGetTier',
      stUpdateSpendAndGetTier: 'updateSpendAndGetTier',
    }),

    show(row) {
      this.cleanSidebar()
      this.showSidebar = true

      if (row) {
        this.tierForm = {
          ...row.item,
          rewards: (row.item.rewards || []).map(reward => ({ ...reward })),
        }
        this.isTierUpdate = true
      }
    },

    onHideSidebar() {
      this.showSidebar = false
      this.cleanSidebar()
    },

    cleanSidebar() {
      this.isTierUpdate = false
      this.tierForm = getInitialSpendAndGetTierForm()
      this.rewardForm = getInitialSpendAndGetRewardForm()
    },

    addReward() {
      const { sku, quantity } = this.rewardForm

      if (!sku?.id || !(Number(quantity) > 0)) {
        this.showInvalidDataMessage({
          message: this.$t('PROMOTION.SPEND_AND_GET.REWARD_PRODUCT_AND_QUANTITY_REQUIRED'),
        })
        return
      }

      this.tierForm.rewards = localListAddItem(this.tierForm.rewards, {
        ...getInitialSpendAndGetRewardForm(),
        skuId: sku.id,
        sku,
        quantity,
      })
      this.rewardForm = getInitialSpendAndGetRewardForm()
    },

    removeReward(row) {
      const { localId, id } = row.item
      this.tierForm.rewards = localListDeleteItem(this.tierForm.rewards, localId || id)
    },

    isTierBusinessValid() {
      const minimumAmount = Number(this.tierForm.minimumAmount)

      // 1. Valor mínimo no intervalo (0,01; 999.999.999,99]
      if (!(minimumAmount > 0) || minimumAmount > 999999999.99) {
        this.showInvalidDataMessage({
          message: this.$t('PROMOTION.SPEND_AND_GET.INVALID_MINIMUM_AMOUNT'),
        })
        return false
      }

      // 2. Valor mínimo não duplicado entre as outras faixas ativas
      const isDuplicated = this.tiers
        .filter(tier => !tier.isDeleted)
        .filter(tier => {
          const isSameByLocalId =
            this.tierForm.localId != null && tier.localId === this.tierForm.localId
          const isSameById = this.tierForm.id != null && tier.id === this.tierForm.id
          return !isSameByLocalId && !isSameById
        })
        .some(tier => Number(tier.minimumAmount) === minimumAmount)

      if (isDuplicated) {
        this.showInvalidDataMessage({
          message: this.$t('PROMOTION.SPEND_AND_GET.DUPLICATE_MINIMUM_AMOUNT'),
        })
        return false
      }

      // 3. Ao menos um brinde ativo
      if (this.activeRewards.length === 0) {
        this.showInvalidDataMessage({
          message: this.$t('PROMOTION.SPEND_AND_GET.TIER_AT_LEAST_ONE_REWARD_REQUIRED'),
        })
        return false
      }

      // 4. Todo brinde ativo com quantidade > 0
      const hasInvalidQuantity = this.activeRewards.some(reward => !(Number(reward.quantity) > 0))
      if (hasInvalidQuantity) {
        this.showInvalidDataMessage({
          message: this.$t('PROMOTION.SPEND_AND_GET.INVALID_REWARD_QUANTITY'),
        })
        return false
      }

      return true
    },

    async onSaveTier() {
      this.$refs.tierSidebarForm.showErrors()
      if (this.$refs.tierSidebarForm.hasErrors) {
        this.showInvalidDataMessage()
        return
      }

      if (!this.isTierBusinessValid()) {
        return
      }

      if (this.isTierUpdate) {
        await this.stUpdateSpendAndGetTier({ tier: this.tierForm })
      } else {
        await this.stAddSpendAndGetTier(this.tierForm)
      }

      this.onHideSidebar()
    },
  },
}
</script>

<style></style>
